import { useState } from 'react';
import { createPortal } from 'react-dom';
import type { MultiColumnData } from '../../types/chat';

export const DataHeatmap = ({ data }: { data: MultiColumnData }) => {
  const [hoveredCell, setHoveredCell] = useState<{
    x: any;
    y: any;
    value: any;
    xPos: number;
    yPos: number;
  } | null>(null);

  const prepareHeatmapData = (): { data: any[]; xValues: any[]; yValues: any[]; zHeader: string } | null => {
    if (!data.data || data.data.length === 0) return null;

    const xValues = Array.from(new Set(data.data.map(row => row[data.headers[0]])));
    const yValues = Array.from(new Set(data.data.map(row => row[data.headers[1]])));

    const heatmapData = yValues.map(yValue => {
      const row: any = { y: yValue };
      xValues.forEach(xValue => {
        const matchingRow = data.data.find(row =>
          row[data.headers[0]] === xValue && row[data.headers[1]] === yValue
        );
        row[xValue] = matchingRow ? matchingRow[data.headers[2]] : 0;
      });
      return row;
    });

    return {
      data: heatmapData,
      xValues,
      yValues,
      zHeader: data.headers[2]
    };
  };

  const heatmapConfig = prepareHeatmapData();

  const allValues = data.data.map(row => row[data.headers[2]]).filter(val => typeof val === 'number');
  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);

  const getColorIntensity = (value: number) => {
    if (typeof value !== 'number') return '#f3f4f6';
    const normalized = (value - minValue) / (maxValue - minValue);
    const lightness = 95 - (normalized * 55); // Purple gradient from light to dark
    const saturation = 30 + (normalized * 40); // Increase saturation for higher values
    return `hsl(270, ${saturation}%, ${lightness}%)`; // Purple hue (270)
  };

  const getTextColor = (backgroundColor: string) => {
    const hslMatch = backgroundColor.match(/hsl\(270, (\d+)%, (\d+)%\)/);
    if (!hslMatch) return '#111827';
    const lightness = parseInt(hslMatch[2]);
    return lightness > 50 ? '#4c3794' : '#ffffff';
  };

  return (
    <div className="mt-4 rounded-xl p-4 border border-gray-200 bg-white">
      <h3 className="font-semibold text-[#4c3794] mb-4 text-base">
        {data.title} - Heatmap Visualization
      </h3>

      {heatmapConfig && heatmapConfig.data.length > 0 ? (
        <div className="overflow-x-auto">
          <div className="inline-block min-w-full border border-gray-200 rounded-lg overflow-hidden">
            <div className="flex border-b-2 border-[#4c3794] bg-purple-50">
              <div className="w-32 p-2 border-r-2 border-[#4c3794] flex items-center justify-start text-left font-semibold text-xs text-[#4c3794]">
                {data.headers[1]} / {data.headers[0]}
              </div>
              {heatmapConfig.xValues.map((xValue: any, index: number) => (
                <div key={index} className={`flex-1 p-2 text-left font-semibold text-xs text-[#4c3794] ${index < heatmapConfig.xValues.length - 1 ? 'border-r border-gray-200' : ''}`}>
                  {String(xValue)}
                </div>
              ))}
            </div>

            {heatmapConfig.data.map((row: any, rowIndex: number) => (
              <div key={rowIndex} className={`flex ${rowIndex < heatmapConfig.data.length - 1 ? 'border-b border-gray-200' : ''}`}>
                <div className="w-32 p-2 border-r-2 border-[#4c3794] flex items-center justify-start text-left font-semibold text-xs text-[#4c3794] bg-purple-50">
                  {String(row.y)}
                </div>
                {heatmapConfig.xValues.map((xValue: any, colIndex: number) => {
                  const backgroundColor = getColorIntensity(row[xValue]);
                  const textColor = getTextColor(backgroundColor);

                  return (
                    <div
                      key={colIndex}
                      className={`flex-1 p-2 text-center text-xs font-medium cursor-pointer transition-all hover:scale-105 hover:shadow-md hover:z-10 relative ${colIndex < heatmapConfig.xValues.length - 1 ? 'border-r border-gray-200' : ''}`}
                      style={{ backgroundColor, color: textColor }}
                      onMouseEnter={(e) => {
                        setHoveredCell({
                          x: xValue,
                          y: row.y,
                          value: row[xValue],
                          xPos: e.clientX,
                          yPos: e.clientY
                        });
                      }}
                      onMouseLeave={() => {
                        setHoveredCell(null);
                      }}
                    >
                      {typeof row[xValue] === 'number' ? (row[xValue] % 1 === 0 ? row[xValue].toString() : row[xValue].toFixed(2)) : row[xValue]}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          <div className="mt-4 flex items-center gap-4 justify-center">
            <span className="text-[#4c3794] font-semibold text-xs">
              {data.headers[2]} Scale:
            </span>
            <div className="flex items-center gap-2 h-6 rounded px-4 border border-purple-200" style={{ background: 'linear-gradient(to right, hsl(270, 30%, 95%), hsl(270, 70%, 40%))' }}>
              <span className="text-[#4c3794] font-semibold text-xs">
                {minValue.toFixed(2)}
              </span>
              <span className="text-[#4c3794] text-xs">
                →
              </span>
              <span className="text-white font-semibold text-xs">
                {maxValue.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex justify-center items-center h-52 text-gray-600 bg-gray-50 rounded-lg">
          <span>No data available for heatmap visualization</span>
        </div>
      )}

      {hoveredCell && createPortal(
        <div
          className="fixed z-[9999] bg-purple-50 rounded-lg border-2 border-[#4c3794] shadow-xl p-3 min-w-[180px] pointer-events-none backdrop-blur-sm"
          style={{
            left: hoveredCell.xPos + 10,
            top: hoveredCell.yPos - 10,
          }}
        >
          <div className="mb-2">
            <div className="font-semibold text-xs text-[#4c3794] mb-1 capitalize">
              {data.headers[0]}:
            </div>
            <div className="text-xs text-gray-700 font-medium">
              {String(hoveredCell.x)}
            </div>
          </div>
          <div className="mb-2">
            <div className="font-semibold text-xs text-[#4c3794] mb-1 capitalize">
              {data.headers[1]}:
            </div>
            <div className="text-xs text-gray-700 font-medium">
              {String(hoveredCell.y)}
            </div>
          </div>
          <div>
            <div className="font-semibold text-xs text-[#4c3794] mb-1 capitalize">
              {data.headers[2]}:
            </div>
            <div className="text-xs text-gray-700 font-medium">
              {typeof hoveredCell.value === 'number' ? (hoveredCell.value % 1 === 0 ? hoveredCell.value.toString() : hoveredCell.value.toFixed(2)) : String(hoveredCell.value)}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
